import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { App as CapApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import './index.css'
import App from './App.tsx'
import { DEFAULT_APP_HOME } from './config/routes'

async function handleMobileAuthDeepLink(url: string) {
  const parsed = new URL(url)
  if (parsed.protocol !== 'io.horton.app:' || parsed.hostname !== 'auth-success') return

  const token = parsed.searchParams.get('token')
  if (!token) return

  try {
    await Browser.close()
  } catch { /* may already be closed */ }

  const resp = await fetch('https://horton.io/api/auth/mobile-token-exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token }),
  })
  const data = await resp.json()
  if (data.success) {
    window.location.href = DEFAULT_APP_HOME
  } else {
    console.error('Mobile token exchange failed:', data.error)
    window.location.href = '/login?error=' + encodeURIComponent(data.error || 'Auth failed')
  }
}

async function initNative() {
  if (!Capacitor.isNativePlatform()) return

  document.documentElement.classList.add('native')

  try {
    await StatusBar.setStyle({ style: Style.Dark })
    if (Capacitor.getPlatform() === 'android') {
      const brandDark = getComputedStyle(document.documentElement).getPropertyValue('--brand-dark').trim() || '#2E2546'
      await StatusBar.setBackgroundColor({ color: brandDark })
      await StatusBar.setOverlaysWebView({ overlay: false })
    }
  } catch (e) {
    console.warn('StatusBar setup failed', e)
  }

  // Deep link handler for mobile OAuth
  CapApp.addListener('appUrlOpen', ({ url }) => {
    handleMobileAuthDeepLink(url)
  })

  // F5 Phase 3: Reminder Contract nagging as real device notifications —
  // schedules the server's firing horizon locally (sound + vibration,
  // works offline), reconciles on resume + every 15 min.
  try {
    const { startReminderNotificationSync } = await import('./native/reminderNotifications')
    startReminderNotificationSync()
  } catch (e) {
    console.warn('Reminder notification sync unavailable', e)
  }

  // Back button: navigate back or minimize
  CapApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back()
    } else {
      CapApp.minimizeApp()
    }
  })

  // Keyboard: toggle body class so bottom nav hides
  try {
    const { Keyboard } = await import('@capacitor/keyboard')
    Keyboard.addListener('keyboardWillShow', () => {
      document.body.classList.add('keyboard-open')
    })
    Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-open')
    })
  } catch { /* keyboard plugin not available */ }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

void initNative()
